import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Rocket, Loader2, Sparkles, PenLine } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function CreateChallengeModal({ onClose }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("ai");
  const [topic, setTopic] = useState("");
  const [manual, setManual] = useState({ title: "", category: "", description: "", total_days: 30 });
  const [busy, setBusy] = useState(false);

  const generateWithAI = async () => {
    if (!topic.trim()) { toast.error("Describe what you want to teach"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/custom-challenges", { topic });
      toast.success("Draft roadmap generated! Review each day in the Builder before publishing.");
      onClose();
      navigate(`/creator/challenges/${data.challenge.id}/builder`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to generate challenge");
    } finally {
      setBusy(false);
    }
  };

  const createManual = async () => {
    if (!manual.title.trim()) { toast.error("Give your challenge a title"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/custom-challenges/manual", manual);
      toast.success("Draft created! Build each day in the Builder, no AI required.");
      onClose();
      navigate(`/creator/challenges/${data.challenge.id}/builder`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create challenge");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="create-challenge-modal">
      <div className="glass-panel rounded-2xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-white text-sm flex items-center gap-2"><Rocket className="w-4 h-4 text-indigo-300" /> New Challenge</h3>
          <button onClick={onClose} data-testid="create-challenge-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex gap-2 mb-4 p-1 rounded-xl bg-white/5">
          <button onClick={() => setMode("ai")} data-testid="create-challenge-mode-ai-btn"
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg transition-colors ${mode === "ai" ? "bg-gradient-to-r from-indigo-500 to-violet-500 text-white" : "text-slate-400 hover:text-white"}`}>
            <Sparkles className="w-3.5 h-3.5" /> AI Creator
          </button>
          <button onClick={() => setMode("manual")} data-testid="create-challenge-mode-manual-btn"
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg transition-colors ${mode === "manual" ? "bg-gradient-to-r from-indigo-500 to-violet-500 text-white" : "text-slate-400 hover:text-white"}`}>
            <PenLine className="w-3.5 h-3.5" /> Manual Creator
          </button>
        </div>

        {mode === "ai" ? (
          <>
            <p className="text-xs text-slate-400 mb-3">Describe any topic — AI drafts a full 30-day roadmap (titles + daily content + quizzes). It's a starting draft — review and edit every day before publishing.</p>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Topic</label>
            <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Docker for Beginners, React Native, UPSC Prelims" data-testid="create-challenge-topic-input"
              className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-indigo-400/50" />
            <button onClick={generateWithAI} disabled={busy} data-testid="create-challenge-generate-btn"
              className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-violet-500 hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {busy ? "Generating..." : "Generate with AI"}
            </button>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-400 mb-3">Build every day from scratch — full control, no AI involved unless you ask for it in the Builder.</p>
            <div className="space-y-2.5">
              <input value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} placeholder="Challenge title" data-testid="create-manual-title-input"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-indigo-400/50" />
              <input value={manual.category} onChange={(e) => setManual({ ...manual, category: e.target.value })} placeholder="Category (e.g. Python, AI, DSA)" data-testid="create-manual-category-input"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-indigo-400/50" />
              <textarea value={manual.description} onChange={(e) => setManual({ ...manual, description: e.target.value })} placeholder="Short description" rows={2} data-testid="create-manual-description-input"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-indigo-400/50" />
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Number of days</label>
                <input type="number" min={1} max={60} value={manual.total_days} onChange={(e) => setManual({ ...manual, total_days: Number(e.target.value) || 30 })} data-testid="create-manual-days-input"
                  className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-indigo-400/50" />
              </div>
            </div>
            <button onClick={createManual} disabled={busy} data-testid="create-manual-submit-btn"
              className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-violet-500 hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} {busy ? "Creating..." : "Create Draft"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
